import type {
  Artist,
  ArtistApplication,
  ArtistApplicationResponse,
  ArtistApplicationSearchResponse,
} from "features/reviewable";
import { applicationKeys } from "features/reviewable/api/queryKeys";
import { useForm } from "react-hook-form";
import {
  useMutation,
  useQuery,
  useInfiniteQuery,
  useQueryClient,
} from "react-query";
import { api, paramsToQueryStr } from "utils/api";
import type {
  AcceptApplicationRequest,
  UpdateApplicationStatusRequest,
  ArtistApplicationSearchRequest,
} from "../types";
import { artistApplicationKeys } from "./queryKeys";

const getArtistApplications = (params: ArtistApplicationSearchRequest) => {
  const query = paramsToQueryStr(params);

  return api.get<ArtistApplicationSearchResponse>(
    `/artists/applications${query}`
  );
};

const useArtistApplications = (params?: ArtistApplicationSearchRequest) =>
  useInfiniteQuery(
    artistApplicationKeys.list(params),
    ({ pageParam }) =>
      getArtistApplications({ searchAfter: pageParam, ...params }),
    {
      getNextPageParam: (lastPage) => lastPage.searchAfter,
    }
  );

const getArtistApplication = (applicationId?: string) =>
  applicationId
    ? api.get<ArtistApplicationResponse>(
        `/artists/applications/${applicationId}`
      )
    : Promise.reject(new Error("Invalid artist application id"));

const useArtistApplication = (applicationId?: string) =>
  useQuery(
    artistApplicationKeys.detail(applicationId),
    () => getArtistApplication(applicationId),
    {
      enabled: !!applicationId,
    }
  );

const acceptArtistApplication = ({
  applicationId,
  slug,
}: AcceptApplicationRequest) =>
  api.post<Artist>(`/artists/applications/${slug}/accept`, {
    body: JSON.stringify({ applicationId }),
    requireAuth: true,
  });

const useAcceptArtistApplication = () => useMutation(acceptArtistApplication);

const updateApplicationStatus = ({
  applicationId,
  status,
}: UpdateApplicationStatusRequest) =>
  api.put<ArtistApplication>(`/artists/applications/${applicationId}`, {
    body: JSON.stringify({ status }),
    requireAuth: true,
  });

const useUpdateApplicationStatus = () => {
  const queryClient = useQueryClient();

  return useMutation(updateApplicationStatus, {
    onSuccess: () => {
      queryClient.invalidateQueries(applicationKeys.base);
    },
  });
};

const useApplicationStatusForm = (application?: ArtistApplication) => {
  const formState = useForm<ArtistApplication>({
    defaultValues: application,
  });
  const mutation = useUpdateApplicationStatus();
  // const onSubmit = handleSubmit((formData) =>
  //   mutate({ id: applicationId, status: formData.status })
  // );
  return { ...formState, mutation };
};

export {
  useArtistApplications,
  useAcceptArtistApplication,
  useArtistApplication,
  useApplicationStatusForm,
};
